/** @file Mix preview settings and the running preview (Animations panel; the viewport poses from it). */
import { createStore } from 'zustand/vanilla';
import type { AnimEvent, AnimState, SkeletonData } from '@pose2d/core/types';
import type { Id } from '@pose2d/format/types';

/** A running preview: `from` loops alone for `lead` s, crossfades to `to` over `mix` s, holds, then starts over. */
export interface MixRun {
  state: AnimState;
  /** The compiled skeleton `state` indexes into (TrackEntry.anim); a recompile needs a rebuilt run. */
  data: SkeletonData;
  /** Animation names (SkeletonData keys them by name). */
  from: string;
  to: string;
  mix: number;
  lead: number;
  /** Seconds since the run (re)started. */
  elapsed: number;
  switched: boolean;
  /** Reused stepState output; cleared after every step. */
  events: AnimEvent[];
}

/** Picked pair, typed seconds and the run. */
export interface MixPreviewState {
  from: Id | null;
  to: Id | null;
  /** Crossfade seconds typed in the panel; null = the document's mix default for the pair. */
  seconds: number | null;
  /** The running preview, mutated in place every tick; null when stopped. */
  run: MixRun | null;
  /** Bumped on every preview tick so subscribers (the viewport's dirty flag) see a change. */
  tick: number;
}

/** Nothing picked, nothing running. */
export const mixPreviewStore = createStore<MixPreviewState>()(() => ({ from: null, to: null, seconds: null, run: null, tick: 0 }));
